import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AcademyAdministratorOperation } from '@domain/AcademyAdministratorOperation';
import { AcademyAdministratorType } from '@domain/AcademyAdministratorType';
import { HttpResponse } from '@domain/HttpResponse';
import { Membership } from '@domain/Membership';
import { GestionService } from '@services/gestion.service';
import type { Cabecera } from '@features/seguimiento/cabecera';
import { Desenlace } from '@features/pago/desenlace';
import {
  OrigenDelPago,
  SalidaDelRetorno,
  SeguimientoDelPago,
} from '@features/pago/origen-del-pago';
import {
  CABECERAS_DE_RENOVACION,
  ESPERA_AGOTADA_DE_RENOVACION,
  NUMERO_DEL_PAGO,
  VOLVER_AL_INICIO,
} from './copy-renovacion';

/**
 * El pago de una **renovacion**, en su membresia (SPEC-016, Fase 2).
 *
 * **La membresia se busca por el `uuid` de la direccion**, que es el mismo que
 * se le mando al middleware como `applicationId` al crear el pago y que quedo
 * escrito en `administrativeData.applicationId`. Es asi porque la URL de retorno
 * se compone **antes** de que el pago exista —va dentro de la peticion que lo
 * crea— y por eso no puede llevar el `paymentId`. El flujo viejo lo resolvia con
 * esta misma consulta.
 */
@Injectable({ providedIn: 'root' })
export class PagoDeRenovacion implements OrigenDelPago {
  private readonly gestion = inject(GestionService);

  async buscar(id: string): Promise<string | null> {
    if (!id) return null;

    const respuesta = (await firstValueFrom(
      this.gestion.sendRequest({
        operation: AcademyAdministratorOperation.SELECT,
        type: AcademyAdministratorType.MEMBERSHIPS,
        payload: {
          options: {
            conditionals: [
              {
                conditionalConnector: null,
                logicOperator: '=',
                evaluatedProperty: 'administrativeData.applicationId',
                parameterName: '@Option1',
                parameterValue: id,
              },
            ],
          },
        },
      }),
    ).catch(() => null)) as HttpResponse | null;

    if (respuesta?.code !== 200 || !Array.isArray(respuesta.response)) return null;
    const [membresia] = respuesta.response as Membership[];
    return membresia?.administrativeData?.paymentInfo?.paymentId ?? null;
  }

  /**
   * **Una renovacion no anota nada al volver, y es la frontera de la spec.**
   *
   * El cierre lo escribe la Logic App de renovaciones: con el pago aprobado pone
   * la membresia en `Active`, suma el cupo del grupo y dispara el correo de
   * bienvenida; con cualquier otra respuesta la marca `paymentFailed`. Escribir
   * aqui seria competir con ella o, peor, decir *"matriculado"* sin que el cupo
   * este sumado.
   */
  async anotar(): Promise<void> {
    return;
  }

  /**
   * Al inicio. **No hay estado de tramite al que ir**: una renovacion no tiene
   * radicado ni linea de tiempo, porque no hay nada que aprobar — y el carnet no
   * se regenera al renovar (AC-7, retirado).
   */
  salida(): SalidaDelRetorno {
    return { enlace: ['/'], etiqueta: VOLVER_AL_INICIO };
  }

  /**
   * **El numero con el que preguntar por el pago** — el `paymentId`, que es por donde
   * el gestor lo busca. Aqui hace falta porque una renovacion no radica nada y
   * esta pantalla es la ultima: sin el, la persona se va sin nada anotado.
   */
  numeroDeSeguimiento(pago: string): SeguimientoDelPago | null {
    return pago ? { etiqueta: NUMERO_DEL_PAGO, numero: pago } : null;
  }

  /**
   * **Y el texto tiene que decir lo mismo que el boton** (encontrado en lab): el de
   * SPEC-013 manda a mirar el estado del tramite, que aqui no existe.
   */
  cabeceras(): Record<Desenlace, Cabecera> {
    return CABECERAS_DE_RENOVACION;
  }

  esperaAgotada(): string {
    return ESPERA_AGOTADA_DE_RENOVACION;
  }
}
